import { toast } from 'react-toastify';

export interface ApiError {
  response?: {
    data?: {
      message?: string;
      error?: string;
      hint?: string;
      errors?: Array<{
        path?: string;
        param?: string;
        msg?: string;
        message?: string;
      }>;
    };
    status?: number;
  };
  message?: string;
}

export const getUserFriendlyMessage = (error: ApiError): string => {
  if (error.response?.data) {
    const errorData = error.response.data;
    
    // Handle validation errors from express-validator
    if (errorData.errors && Array.isArray(errorData.errors)) {
      const validationErrors = errorData.errors.map((err) => {
        const field = err.path || err.param || 'field';
        const message = err.msg || err.message || 'Invalid value';
        return `${field}: ${message}`;
      }).join(', ');
      
      return `Validation Error: ${validationErrors}`;
    }
    // Handle single error message
    else if (errorData.message) {
      return errorData.hint ? `${errorData.message}\n${errorData.hint}` : errorData.message;
    }
    // Handle error with details
    else if (errorData.error) {
      return errorData.error;
    }
  }
  // Handle network errors
  else if (error.message) {
    if (error.message.includes('Network Error') || error.message.includes('Failed to fetch')) {
      return navigator.onLine 
        ? 'Network connection failed. Please check your internet connection.'
        : 'You are currently offline. Please check your internet connection.';
    } else if (error.message.includes('timeout')) {
      return 'Request timed out. Please try again.';
    } else {
      return error.message;
    }
  }
  
  // Handle offline errors (check if error has isOffline flag)
  if ((error as any).isOffline || !navigator.onLine) {
    return 'You are currently offline. Please check your internet connection.';
  }
  
  // Handle network errors (check if error has isNetworkError flag)
  if ((error as any).isNetworkError) {
    return (error as any).userMessage || 'Network connection error. Please check your internet connection.';
  }
  
  return 'An unexpected error occurred';
};

export const showErrorToast = (error: ApiError, context: string) => {
  const errorMessage = getUserFriendlyMessage(error);
  
  toast.error(
    <div>
      <div className="font-semibold mb-1"> {context} Failed</div>
      <div className="text-sm">{errorMessage}</div>
      {error.response?.status && (
        <div className="text-xs text-gray-500 mt-1">
          Status: {error.response.status}
        </div>
      )}
    </div>,
    { 
      autoClose: 8000,
      closeButton: true,
      position: "top-center"
    }
  );
};

export const showWarningToast = (message: string, context: string) => {
  toast.warning(
    <div>
      <div className="font-semibold mb-1"> {context}</div>
      <div className="text-sm">{message}</div>
    </div>,
    { 
      autoClose: 5000,
      closeButton: true,
      position: "top-center"
    }
  );
};

export const showSuccessToast = (message: string, context: string) => {
  toast.success(
    <div>
      <div className="font-semibold mb-1"> {context}</div>
      <div className="text-sm">{message}</div>
    </div>,
    { 
      autoClose: 3000,
      closeButton: true,
      position: "top-center"
    }
  );
};

export const logError = (error: ApiError, context: string) => {
  console.error(`${context} error:`, error);
  if (error.response?.data) {
    console.error(`${context} server body:`, error.response.data);
  }
};

export const extractErrorDetails = (error: ApiError) => {
  if (error.response?.data) {
    const errorData = error.response.data;
    
    if (errorData.errors && Array.isArray(errorData.errors)) {
      return {
        type: 'validation',
        errors: errorData.errors,
        message: errorData.message || 'Validation failed'
      };
    }
    
    return {
      type: 'api',
      message: errorData.message || errorData.error || 'API error',
      status: error.response.status
    };
  }
  
  return {
    type: 'network',
    message: error.message || 'Network error'
  };
};

export const isValidationError = (error: ApiError): boolean => {
  return error.response?.data?.errors && Array.isArray(error.response.data.errors);
};

export const isAuthError = (error: ApiError): boolean => {
  return error.response?.status === 401 || error.response?.status === 403;
};